/// <reference path="../../../node_modules/@arcgis/core/interfaces.d.ts" />
import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Layer from "@arcgis/core/layers/Layer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";
import * as geometryJsonUtils from "@arcgis/core/geometry/support/jsonUtils";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import Color from "@arcgis/core/Color";
import esriConfig from "@arcgis/core/config";
import IdentityManager from "@arcgis/core/identity/IdentityManager";

import LayerList from "@arcgis/core/widgets/LayerList";
import Slider from "@arcgis/core/widgets/Slider";
import ActionButton from "@arcgis/core/support/actions/ActionButton";
import Legend from "@arcgis/core/widgets/Legend";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import LocalBasemapsSource from "@arcgis/core/widgets/BasemapGallery/support/LocalBasemapsSource";
import Basemap from "@arcgis/core/Basemap";
import TileLayer from "@arcgis/core/layers/TileLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import ImageryTileLayer from "@arcgis/core/layers/ImageryTileLayer";
import Measurement from "@arcgis/core/widgets/Measurement";
import Expand from "@arcgis/core/widgets/Expand";
import Home from "@arcgis/core/widgets/Home";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import Zoom from "@arcgis/core/widgets/Zoom";
import Search from "@arcgis/core/widgets/Search";

import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import Point from "@arcgis/core/geometry/Point";
import type { ClickEvent as ViewClickEvent } from "@arcgis/core/views/input/types";
import * as queryModule from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";

import { ArcGISMapProps, ArcGISMapHandle, AppJsonConfig } from "../../types/config";

import { drawWidget } from "./custom-widgets/Draw";
import { googleStreetView } from "./custom-widgets/GoogleStreetView";
import { installCompactCoordinate } from "./custom-widgets/Coordinate";
import streetViewIcon from "./custom-widgets/images/googleStreetIcon.png";
import esriGreenPin from "./custom-widgets/images/esriGreenPin16x26.png";

import tnImagery2018 from "./images/tnimagerybm2018.png";
import tnImagery2013 from "./images/tnimagerybm2013.png";
import tnImagery2005 from "./images/tnimagerybm2005.png";
import harrisonBasemapThumb from "./images/HarrisonBasemapThumb.png";
import harrisonImgThumb from "./images/HarrisonImgThumb.png";
import esriStreets from "./images/esristreetsbm.png";
import esriTopo from "./images/esritopobm.png";
import esriLightGray from "./images/esrilightgray.png";
import googleHybrid from "./images/googlehybridbm.png";
import esriimagerybm from "./images/esriimagerybm.png";

import config from "../../config/config.json";
import "./styles/Map.css"
import * as CSS from "csstype";

// Self-host ArcGIS's runtime assets (locale strings, icons, wasm, worker chunks) from our
// own build output instead of the js.arcgis.com CDN default - see scripts/select-county.js's
// ensureArcgisAssets(), which copies node_modules/@arcgis/core/assets into public/assets.
esriConfig.assetsPath = `${process.env.PUBLIC_URL || ""}/assets`;

const MapDivStyle: CSS.Properties = { height: "100%", width: "100%", position: "relative" };

const ArcGISMap = forwardRef<ArcGISMapHandle, ArcGISMapProps>(({ itemId, onMapIdentify }, ref) => {
    const mapDiv = useRef<HTMLDivElement>(null);
    const viewRef = useRef<MapView | null>(null);
    const highlightLayerRef = useRef<GraphicsLayer | null>(null);
    const onMapIdentifyRef = useRef(onMapIdentify);
    useEffect(() => { onMapIdentifyRef.current = onMapIdentify; }, [onMapIdentify]);

    useEffect(() => {
        if (!mapDiv.current) return;

        esriConfig.portalUrl = config.map.portalUrl;

        IdentityManager.registerToken({
            server: config.map.portalUrl,
            token: "",
            expires: Date.now() + 365 * 24 * 60 * 60 * 1000
        } as any);

        const webmap = new WebMap({ portalItem: { id: itemId } });
        const view = new MapView({
            container: mapDiv.current,
            map: webmap,
        });
        viewRef.current = view;

        const topRightRow = document.createElement("div");
        topRightRow.className = "custom-top-right-row";
        Object.assign(topRightRow.style, { display: "flex", gap: "8px", alignItems: "center", padding: "8px" });
        view.ui.add(topRightRow, { position: "top-right", index: 0 });

        const styleEl = document.createElement("style");
        styleEl.textContent = `
          .custom-top-right-row .expand-wrap { position: relative; }
          .custom-top-right-row .expand-wrap .esri-expand__panel {
            right: 0 !important; left: auto !important; top: calc(100% + 8px);
            max-height: min(60vh, calc(100% - 56px)); max-width: min(600px, calc(100vw - 32px));
            overflow: auto; box-sizing: border-box;
          }
          .custom-top-right-row .esri-widget--button { margin: 0; }
          .esri-ui, .esri-ui-corner, .esri-ui-top-right, .esri-ui-inner-container { overflow: visible !important; }
          .esri-layer-list, .esri-basemap-gallery { position: static !important; margin-top: 0 !important; }
          .esri-icon-layer-list, .esri-icon-basemap, .esri-icon-feature-layer, .esri-icon-radio-unchecked { position: static !important; top:auto !important; left:auto !important; }

          .exb-btn { width:32px; height:32px; border:1px solid #dcdcdc; border-radius:4px; background:#fff; cursor:pointer; display:grid; place-items:center; }
          .exb-btn svg { width:18px; height:18px; fill:#2b2b2b; }

          .exb-pop { z-index:5; border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,.15); padding:12px; }
          .exb-row { display:grid; grid-template-columns:auto 1fr auto; gap:8px; align-items:center; margin:8px 0; }
          .exb-sw { display:grid; grid-template-columns: repeat(10, 18px); gap:6px; margin:8px 0; }
          .exb-swatch { width:18px; height:18px; border-radius:50%; border:1px solid #ccc; cursor:pointer; }
          .exb-title { font-weight:600; margin-bottom:8px; display:flex; align-items:center; gap:8px; }
          .exb-prev { width:24px; height:24px; border-radius:50%; border:2px solid #2b2b2b; background:#0079c133; }

          .esri-button { padding:6px 10px; border:1px solid #dcdcdc; background:#fff; border-radius:4px; cursor:pointer; font-size:12px; }
          .esri-button:disabled { opacity:.5; cursor:default; }
        `;
        mapDiv.current.appendChild(styleEl);

        const wrap = () => {
            const w = document.createElement("div");
            w.className = "expand-wrap";
            topRightRow.appendChild(w);
            return w;
        };

        view.when(async () => {
            const hl = new GraphicsLayer({ listMode: "hide" });
            webmap.add(hl);
            highlightLayerRef.current = hl;

            const legend = new Legend({ view });
            const zoomToAction = new ActionButton({ id: "zoom-to", title: "Zoom to", icon: "zoom-to-object" });
            const transparencyAction = new ActionButton({ id: "transparency", title: "Transparency", icon: "sliders-horizontal" });

            const layerList = new LayerList({
                view,
                visibilityAppearance: "checkbox",
                dragEnabled: true,
                listItemCreatedFunction: (event) => {
                    const item = event.item;
                    const layer = item.layer as Layer | null;
                    if (!layer) return;
                    // WMS sublayers don't support individual opacity — only show transparency on top-level layers
                    const isSublayer = !!item.parent || (layer as any).type === "wms-sublayer" || (layer as any).sublayerId != null;
                    item.actionsSections = [isSublayer ? [zoomToAction] : [zoomToAction, transparencyAction]] as any;

                    // Show legend inline by default; transparency slider shown on action click
                    item.panel = { content: "legend", open: true } as any;
                },
            });

            layerList.on("trigger-action", (event: any) => {
                const { action, item } = event;
                const layer = item.layer as any;
                if (!layer) return;
                if (action.id === "zoom-to") {
                    view.goTo(layer.fullExtent).catch(() => {});
                } else if (action.id === "transparency") {
                    // Replace panel with opacity slider, or toggle back to legend
                    const currentContent = (item.panel as any)?.content;
                    if (currentContent === "legend" || currentContent == null) {
                        const slider = new Slider({
                            min: 0,
                            max: 1,
                            precision: 2,
                            values: [layer.opacity ?? 1],
                            visibleElements: { rangeLabels: true },
                        });
                        slider.on("thumb-drag", (e: { value: number }) => { layer.opacity = e.value; });
                        item.panel = { content: slider, open: true } as any;
                    } else {
                        item.panel = { content: "legend", open: true } as any;
                    }
                }
            });

            // Map thumbnail filenames to imported images
            const thumbnailMap: Record<string, string> = {
                "images/esriimagerybm.png": esriimagerybm,
                "images/tnimagerybm2018.png": tnImagery2018,
                "images/tnimagerybm2013.png": tnImagery2013,
                "images/tnimagerybm2005.png": tnImagery2005,
                "images/HarrisonBasemapThumb.png": harrisonBasemapThumb,
                "images/HarrisonImgThumb.png": harrisonImgThumb,
                "images/esristreetsbm.png": esriStreets,
                "images/esritopobm.png": esriTopo,
                "images/esrilightgray.png": esriLightGray,
                "images/googlehybridbm.png": googleHybrid
            };

            // Create custom basemaps from config
            const bgConfig = (config as unknown as AppJsonConfig).basemapGallery!;
            const customBasemaps = await Promise.all((bgConfig.basemaps || []).map(async bmConfig => {
                const baseLayers = await Promise.all(bmConfig.layers.map(async layerConfig => {
                    // Try ImageryTileLayer for ImageServer URLs (supports client-side reprojection)
                    if (layerConfig.url.includes("/ImageServer")) {
                        try {
                            const imgTileLayer = new ImageryTileLayer({ url: layerConfig.url });
                            await imgTileLayer.load();
                            return imgTileLayer;
                        } catch (error) {
                            console.warn(`ImageryTileLayer failed for ${layerConfig.url}, trying TileLayer`);
                        }
                    }

                    // Try to load as TileLayer
                    const tileLayer = new TileLayer({ url: layerConfig.url });
                    try {
                        await tileLayer.load();
                        // Check if it has tileInfo (is a cached service)
                        if (tileLayer.tileInfo) {
                            return tileLayer;
                        }
                    } catch (error) {
                        console.warn(`TileLayer failed for ${layerConfig.url}, trying MapImageLayer`);
                    }

                    // Fall back to MapImageLayer for dynamic services
                    return new MapImageLayer({ url: layerConfig.url });
                }));

                return new Basemap({
                    baseLayers: baseLayers,
                    title: bmConfig.title,
                    thumbnailUrl: thumbnailMap[bmConfig.thumbnailUrl] || bmConfig.thumbnailUrl
                });
            }));


            const wmsBasemaps = bgConfig.wmsBasemaps ? await Promise.all(
                bgConfig.wmsBasemaps.map(async (wmsConfig: any) => {

                    let wmsUrl = wmsConfig.url;
                    if (process.env.NODE_ENV === 'development') {
                        const url = new URL(wmsConfig.url);
                        wmsUrl = `http://localhost:3000/wms-proxy${url.pathname}`;
                    } else if (
                        window.location.hostname === 'testing.geopowered.com' ||
                        (config as any).wmsProxyHost === window.location.hostname
                    ) {
                        // Shared staging host serves every county under its own PUBLIC_URL subpath.
                        // Some counties (e.g. Wilson) also need their own production host proxied -
                        // their imagery endpoint requires the IIS-injected Referer/Origin headers
                        // that only the /wms-proxy rewrite rule (see that county's web.config) provides.
                        const url = new URL(wmsConfig.url);
                        wmsUrl = `${window.location.origin}${process.env.PUBLIC_URL}/wms-proxy${url.pathname}`;
                    }

                    const corsServers = (esriConfig.request as any).corsEnabledServers || [];
                    ['https://tnmap.giza.cloud', 'https://tnmap-wms.giza.cloud'].forEach(url => {
                        if (!corsServers.includes(url)) corsServers.push(url);
                    });
                    (esriConfig.request as any).corsEnabledServers = corsServers;

                    const visibleLayers = wmsConfig.layers.map((layer: any) => layer.name);
                    const layerInfos = wmsConfig.layers.map((layer: any) => ({
                        name: layer.name,
                        title: layer.title
                    }));

                    const wmsLayer = new WMSLayer({
                        url: wmsUrl,
                        title: wmsConfig.title,
                        imageFormat: "png",
                        version: "1.3.0",
                        visible: true,
                        spatialReference: { wkid: 3857 },
                        customParameters: {
                            FORMAT: "image/png"
                        },
                        fullExtent: {
                            xmin: -20037508.342787,
                            ymin: -20037508.342787,
                            xmax: 20037508.342787,
                            ymax: 20037508.342787,
                            spatialReference: { wkid: 3857 }
                        },
                        resourceInfo: {
                            copyright: wmsConfig.copyright,
                            description: wmsConfig.description,
                            extent: {
                                xmin: -20037508.342787,
                                ymin: -20037508.342787,
                                xmax: 20037508.342787,
                                ymax: 20037508.342787,
                                spatialReference: { wkid: 3857 }
                            },
                            layerInfos: layerInfos,
                            spatialReferences: [3857],
                            version: "1.3.0"
                        },
                        visibleLayers: visibleLayers,
                        sublayers: wmsConfig.layers.map((layer: any) => ({
                            name: layer.name,
                            title: layer.title,
                            visible: true
                        }))
                    } as any);

                    // Set these unconditionally (not just on load success) - a load() failure
                    // shouldn't silently drop the metadata the print interceptor depends on to
                    // find this layer and build a valid WMS baseMap request later.
                    (wmsLayer as any).mapUrl = wmsUrl;
                    (wmsLayer as any)._originalWmsUrl = wmsConfig.url;
                    (wmsLayer as any)._wmsLayerInfos = layerInfos;

                    try {
                        await wmsLayer.load();
                    } catch (error) {
                        console.error('[WMS] Failed to load WMS layer:', wmsConfig.title, error);
                    }

                    const wmsBasemap = new Basemap({
                        baseLayers: [wmsLayer],
                        title: wmsConfig.title,
                        thumbnailUrl: thumbnailMap[wmsConfig.thumbnailUrl] || wmsConfig.thumbnailUrl
                    });
                    (wmsBasemap as any)._showFirst = wmsConfig.showFirst;
                    return wmsBasemap;
                })
            ) : [];

            // Order is driven by config.json: basemaps array order, then wmsBasemaps -
            // except any basemap flagged showFirst (config.json), which jumps to the front.
            const combinedBasemaps = [...customBasemaps, ...wmsBasemaps];
            const pinnedBasemaps = combinedBasemaps.filter((b: any) => b._showFirst);
            const unpinnedBasemaps = combinedBasemaps.filter((b: any) => !b._showFirst);
            const allCustomBasemaps = [...pinnedBasemaps, ...unpinnedBasemaps];

            // Build the basemap list: webmap default basemap first, then custom basemaps
            const webmapDefaultBasemap = webmap.basemap ? webmap.basemap.clone() : null;
            if (webmapDefaultBasemap && bgConfig.webmapBasemapTitle) {
                webmapDefaultBasemap.title = bgConfig.webmapBasemapTitle;
                const webmapThumb = (bgConfig as any).webmapBasemapThumbnailUrl;
                if (webmapThumb) {
                    webmapDefaultBasemap.thumbnailUrl = thumbnailMap[webmapThumb] || webmapThumb;
                }
            }

            const galleryBasemaps = [
                ...(webmapDefaultBasemap && !bgConfig.hideWebmapBasemap ? [webmapDefaultBasemap] : []),
                ...allCustomBasemaps,
            ];

            const basemapGallery = new BasemapGallery({
                view,
                source: new LocalBasemapsSource({ basemaps: galleryBasemaps }),
            });

            // The gallery swaps in a brand-new WMSLayer instance (new auto-generated id) on every
            // basemap change, and the Print widget's own snapshot of the map can lag behind that
            // swap by a tick - so matching print's serialized layer id back to whatever's
            // currently in webmap.layers is a race. Track the *current* WMS layer's metadata
            // directly instead; there's only ever one active WMS basemap layer at a time, so the
            // print interceptor can just read this rather than looking anything up.
            const activeWmsLayerMeta: { current: { originalUrl: string; layerInfos: any } | null } = { current: null };

            basemapGallery.watch("activeBasemap", (newBasemap) => {
                if (!newBasemap) return;

                // Remove any existing WMS layers from the map
                const layersToRemove = webmap.layers.filter((layer) => {
                    return layer.type === "wms" && (layer as any)._isBasemapLayer;
                });
                layersToRemove.forEach((layer) => {
                    webmap.layers.remove(layer);
                });

                // Check if the new basemap contains a WMS layer
                const wmsBaseLayer = newBasemap.baseLayers.find((layer: any) => layer.type === "wms");
                if (wmsBaseLayer) {
                    // Create a fresh WMS layer instance with the same properties
                    const freshLayer = new WMSLayer({
                        url: wmsBaseLayer.url,
                        title: wmsBaseLayer.title,
                        imageFormat: wmsBaseLayer.imageFormat,
                        version: wmsBaseLayer.version,
                        visible: true,
                        spatialReference: wmsBaseLayer.spatialReference,
                        customParameters: (wmsBaseLayer as any).customParameters,
                        fullExtent: wmsBaseLayer.fullExtent,
                        resourceInfo: (wmsBaseLayer as any).resourceInfo,
                        visibleLayers: (wmsBaseLayer as any).visibleLayers,
                        sublayers: wmsBaseLayer.sublayers
                    } as any);

                    (freshLayer as any)._isBasemapLayer = true;
                    (freshLayer as any)._originalWmsUrl = (wmsBaseLayer as any)._originalWmsUrl || wmsBaseLayer.url;
                    (freshLayer as any)._wmsLayerInfos = (wmsBaseLayer as any)._wmsLayerInfos;

                    activeWmsLayerMeta.current = {
                        originalUrl: (freshLayer as any)._originalWmsUrl,
                        layerInfos: (freshLayer as any)._wmsLayerInfos
                    };

                    // Add the fresh WMS layer to the map at index 0 (bottom)
                    webmap.layers.add(freshLayer, 0);
                } else {
                    activeWmsLayerMeta.current = null;
                }
            });

            // @ts-ignore - module resolves at runtime via bundler
            await import("@arcgis/map-components/components/arcgis-print");
            const printWidgetContainer = document.createElement("div");
            const printEl = document.createElement("arcgis-print") as HTMLElement & {
                view: MapView;
                printServiceUrl: string;
                templateOptions: any;
                destroy: () => Promise<void>;
            };
            printEl.view = view;
            printEl.printServiceUrl = config.exportWebMapService;
            printEl.templateOptions = { legendEnabled: false };
            printWidgetContainer.appendChild(printEl);

            // Intercept print requests to fix up how the WMS basemap layer gets sent to the GP
            // service. Two modes, selected per-county by config.json's wmsPrintRawOperationalLayer
            // flag:
            //  - Default (Jackson, Mt Juliet, Sumner, Tipton): the original fix from when this was
            //    built - move the WMS layer from operationalLayers into baseMap. This is what makes
            //    Tipton's print work today; untouched, not re-validated against those other
            //    counties, so don't change it without testing each of them.
            //  - wmsPrintRawOperationalLayer (Wilson only): keep the layer as a plain operational
            //    layer pointed at its raw upstream URL, matching the legacy Web AppBuilder app
            //    (confirmed working). Wilson's GP service silently drops WMS basemapLayers instead
            //    of erroring, so the baseMap-move approach doesn't work for it specifically - see
            //    git history / conversation log for the full investigation.
            if (!esriConfig.request.interceptors) {
                esriConfig.request.interceptors = [];
            }
            esriConfig.request.interceptors.push({
                urls: config.exportWebMapService,
                before: (params: any) => {
                    const query = params.requestOptions?.query;
                    const webMapStr = query?.Web_Map_as_JSON;
                    if (!webMapStr) return;
                    try {
                        const webMapJson = JSON.parse(webMapStr);
                        if (!webMapJson.operationalLayers) return;

                        const wmsEntries = webMapJson.operationalLayers.filter(
                            (ol: any) => ol.type === "wms"
                        );
                        if (wmsEntries.length === 0) return;

                        const wms = wmsEntries[0];

                        if ((config as any).wmsPrintRawOperationalLayer) {
                            // --- Wilson-only path ---
                            const activeMeta = activeWmsLayerMeta.current;
                            if (!activeMeta?.originalUrl) return;

                            // Must use the raw Giza URL, not our own /wms-proxy - IIS logs already
                            // confirmed ArcGIS Server never makes outbound requests back to our own
                            // *.geopowered.com hosts, so routing through our proxy just produces a
                            // silent blank image (no GP error, empty imagery). The GP service
                            // forwards whatever Referer/Origin the incoming print request had, and
                            // Giza only allowlists https://wilsontn.geopowered.com/ - this only
                            // works when printed from that real production host, not from
                            // testing.geopowered.com. Fixing testing requires Giza adding
                            // testing.geopowered.com to their referrer allowlist - see chat/ticket
                            // with their solutions engineer.
                            const rebuilt = {
                                id: "wmsBasemapLayer",
                                title: wms.title,
                                opacity: wms.opacity ?? 1,
                                minScale: wms.minScale ?? 0,
                                maxScale: wms.maxScale ?? 0,
                                url: activeMeta.originalUrl,
                                type: "wms",
                                version: wms.version || "1.3.0",
                                transparentBackground: wms.transparentBackground ?? true,
                                visibleLayers: wms.visibleLayers
                            };

                            // Put it FIRST (bottom of the draw order), matching WAB - this is an
                            // aerial basemap, everything else (boundaries, labels) needs to draw
                            // on top of it, not be covered by it.
                            webMapJson.operationalLayers = [rebuilt].concat(
                                webMapJson.operationalLayers.filter((ol: any) => ol.type !== "wms")
                            );

                            const stripLatestWkid = (sr: any) => { if (sr && typeof sr === "object") delete sr.latestWkid; };
                            stripLatestWkid(webMapJson.mapOptions?.spatialReference);
                            stripLatestWkid(webMapJson.mapOptions?.extent?.spatialReference);

                            query.Web_Map_as_JSON = JSON.stringify(webMapJson);
                        } else {
                            // --- Original path (Jackson, Mt Juliet, Sumner, Tipton) ---
                            webMapJson.operationalLayers = webMapJson.operationalLayers.filter(
                                (ol: any) => ol.type !== "wms"
                            );

                            // Prefer activeWmsLayerMeta (set synchronously in the gallery's
                            // "activeBasemap" watch handler) over an id-based lookup into
                            // webmap.layers - the gallery swaps in a brand-new WMSLayer instance
                            // with a new auto-generated id on every basemap change, and print's own
                            // snapshot of the map can lag that swap by a tick, so matching wms.id
                            // back into webmap.layers is a race. When it loses that race it falls
                            // back to wms.url, which in dev is the localhost wms-proxy URL -
                            // unreachable from ArcGIS Server, causing "Invalid input" print failures.
                            const mapLayer = webmap.layers.find(
                                (l: any) => l.id === wms.id && l._originalWmsUrl
                            ) as any;
                            const url = activeWmsLayerMeta.current?.originalUrl || mapLayer?._originalWmsUrl || wms.url;

                            webMapJson.baseMap = {
                                title: wms.title || "WMS Basemap",
                                baseMapLayers: [{
                                    id: wms.id,
                                    title: wms.title,
                                    url: url,
                                    type: "wms",
                                    visibility: true,
                                    opacity: wms.opacity ?? 1,
                                    visibleLayers: wms.visibleLayers,
                                    version: wms.version || "1.3.0"
                                }]
                            };

                            query.Web_Map_as_JSON = JSON.stringify(webMapJson);
                        }
                    } catch (e) {
                        console.error("[Print] Failed to modify print request:", e);
                    }
                }
            });

            const scaleBar = new ScaleBar({ view, unit: "dual", style: "ruler" });
            view.ui.add(scaleBar, { position: "bottom-left", index: 0 });

            view.ui.components = [];
            view.attributionVisible = true;

            const topLeftRow = document.createElement("div");
            topLeftRow.className = "custom-top-left-row";
            Object.assign(topLeftRow.style, {
                display: "flex",
                gap: "8px",
            });
            view.ui.add(topLeftRow, { position: "top-left", index: 0 });

            const zoomNode = document.createElement("div");

            topLeftRow.appendChild(zoomNode);

            const zoom = new Zoom({ view, container: zoomNode });

            if ((config as unknown as AppJsonConfig).enabledModules?.general?.geocoder) {
                const searchWrap = document.createElement("div");
                Object.assign(searchWrap.style, { minWidth: "280px", maxWidth: "360px" });
                const searchNode = document.createElement("div");
                searchWrap.appendChild(searchNode);
                topLeftRow.appendChild(searchWrap);

                new Search({
                    view,
                    container: searchNode,
                    includeDefaultSources: true,
                    popupEnabled: false,
                    allPlaceholder: "ArcGIS World Geocoder",
                    locationEnabled: false,
                });
            }

            const home = new Home({ view });
            view.ui.add(home, { position: "top-left", index: 1 });

            const gsv = googleStreetView(view, {
                iconUrl: streetViewIcon,
                startAtCenter: true,
                resizable: true,
            });

            const draw = drawWidget(view);

            // Detect mobile for widget placement
            const isMobile = window.innerWidth <= 768;
            const widgetPlacement = isMobile ? "right" : "bottom";

            const drawExpand = new Expand({
                view,
                content: draw.element as any,
                group: "top-right-tools",
                expandTooltip: "Draw",
                expandIcon: "annotate-tool",
                collapseIcon: "annotate-tool",
                placement: "bottom",
                container: wrap(),
                mode: "floating"
            });

            // keep visibility in sync
            draw.setVisible(drawExpand.expanded);
            drawExpand.watch("expanded", (v: boolean) => draw.setVisible(!!v));

            const gsvExpand = new Expand({
                view,
                content: gsv.element as any,
                expandTooltip: "Street View",
                expanded: false,
                expandIcon: "person-view",
                collapseIcon: "person-view",
                mode: "floating"
            });
            view.ui.add(gsvExpand, { position: "top-left", index: 2 });
            gsv.setVisible(gsvExpand.expanded);
            gsvExpand.watch("expanded", (v) => gsv.setVisible(!!v));

            const measurementDiv = document.createElement("div");
            measurementDiv.style.minWidth = "fit-content";
            measurementDiv.style.minHeight = "fit-content";

            const measurement = new Measurement({
                view,
                container: measurementDiv,
                linearUnit: "feet",
                areaUnit: "acres",
            });

            // Create coordinate display container
            const coordDiv = document.createElement("div");
            coordDiv.className = "coordinate-display";
            coordDiv.style.cssText = "display: none; padding: 12px; min-width: 320px;";
            coordDiv.innerHTML = `
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                    <div style="font-weight: 600; font-size: 14px;">Measurement Result</div>
                    <select class="coord-format-select esri-select" style="padding: 0px 8px; font-size: 10px; width: 25%;">
                        <option value="degrees">Degrees</option>
                        <option value="dms">DMS</option>
                    </select>
                </div>
                <table style="width: 100%; border-collapse: collapse;">
                    <thead>
                        <tr style="border-bottom: 1px solid #ccc;">
                            <th style="text-align: left; padding: 4px;"></th>
                            <th style="text-align: right; padding: 4px;">Latitude</th>
                            <th style="text-align: right; padding: 4px;">Longitude</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="coord-mouse-row">
                            <td style="padding: 4px; text-align: center;">
                                <span class="esri-icon esri-icon-cursor" style="font-size: 16px;"></span>
                            </td>
                            <td class="coord-mouse-lat" style="text-align: right; padding: 4px; font-family: monospace; font-size: 12px;">---</td>
                            <td class="coord-mouse-lon" style="text-align: right; padding: 4px; font-family: monospace; font-size: 12px;">---</td>
                        </tr>
                        <tr class="coord-pin-row">
                            <td style="padding: 4px; text-align: center;">
                                <img src="${esriGreenPin}" alt="pin" style="width: 16px; height: 26px; vertical-align: middle;" />
                            </td>
                            <td class="coord-pin-lat" style="text-align: right; padding: 4px; font-family: monospace; font-size: 12px;">---</td>
                            <td class="coord-pin-lon" style="text-align: right; padding: 4px; font-family: monospace; font-size: 12px;">---</td>
                        </tr>
                    </tbody>
                </table>
            `;

            const mBar = document.createElement("div");
            mBar.style.cssText = "display: flex; gap: 8px; margin: 8px; align-items: center;";

            const btnDist = document.createElement("button");
            btnDist.className = "esri-button";
            btnDist.title = "Distance";
            btnDist.setAttribute("aria-label", "Distance");
            btnDist.innerHTML = `<span class="esri-icon esri-icon-measure-line" aria-hidden="true"></span>`;

            const btnArea = document.createElement("button");
            btnArea.className = "esri-button";
            btnArea.title = "Area";
            btnArea.setAttribute("aria-label", "Area");
            btnArea.innerHTML = `<span class="esri-icon esri-icon-measure-area" aria-hidden="true"></span>`;

            const btnCoord = document.createElement("button");
            btnCoord.className = "esri-button";
            btnCoord.title = "Coordinates";
            btnCoord.setAttribute("aria-label", "Coordinates");
            btnCoord.innerHTML = `<span class="esri-icon esri-icon-map-pin" aria-hidden="true"></span>`;

            const btnClear = document.createElement("button");
            btnClear.className = "esri-button";
            btnClear.title = "Clear";
            btnClear.setAttribute("aria-label", "Clear");
            btnClear.innerHTML = `<span class="esri-icon esri-icon-trash" aria-hidden="true"></span>`;

            mBar.append(btnDist, btnArea, btnCoord, btnClear);
            styleEl.textContent += `
                .esri-button { display:inline-grid; place-items:center; width:32px; height:32px; padding:0; }
                .esri-button .esri-icon { font-size:16px; line-height:1; }
                .custom-top-right-row .esri-button,
                .custom-top-right-row .esri-button:hover,
                .custom-top-right-row .esri-button:focus,
                .custom-top-right-row .esri-button:active {
                  color: #2b2b2b !important;
                  background: #fff !important;
                  border: 1px solid #dcdcdc !important;
                }

                .custom-top-right-row .esri-button:hover { background: #f5f5f5 !important; }
                .custom-top-right-row .esri-button[aria-pressed="true"] {
                  background: #e6f2fb !important;
                  color: #005e95 !important;
                  border-color: #9bc8e5 !important;
                }

                .custom-top-right-row .esri-button .esri-icon { font-size: 16px; line-height: 1; }
            `;

            const measureWrap = document.createElement("div");
            measureWrap.append(mBar, measurementDiv, coordDiv);

            const measureExpand = new Expand({
                view,
                content: measureWrap,
                group: "top-right-tools",
                expandTooltip: "Measure",
                expandIcon: "measure",
                collapseIcon: "measure",
                placement: widgetPlacement,
                container: wrap(),
                mode: "floating"
            });
            const legendExpand = new Expand({ view, content: legend, group: "top-right-tools", expandTooltip: "Legend", collapseIcon: "legend", placement: widgetPlacement, container: wrap(), mode: "floating" });
            const llExpand = new Expand({ view, content: layerList, group: "top-right-tools", expandTooltip: "Layers", collapseIcon: "layers", placement: widgetPlacement, container: wrap(), mode: "floating" });
            const bgExpand = new Expand({ view, content: basemapGallery, group: "top-right-tools", expandTooltip: "Basemaps", collapseIcon: "basemap", placement: widgetPlacement, container: wrap(), mode: "floating" });

            function setTool(tool: "distance" | "area" | null) {
                measurement.clear();
                (measurement as any).activeTool = tool;
            }

            const m = measurement as any;
            let coordMode = false;
            let pointerMoveHandler: IHandle | null = null;
            let clickHandler: IHandle | null = null;
            let coordPinGraphic: Graphic | null = null;
            let coordFormat: "degrees" | "dms" = "degrees";
            let lastPinLat = 0;
            let lastPinLon = 0;

            // Helper function to convert decimal degrees to DMS
            function toDMS(decimal: number, isLat: boolean): string {
                const absolute = Math.abs(decimal);
                const degrees = Math.floor(absolute);
                const minutesDecimal = (absolute - degrees) * 60;
                const minutes = Math.floor(minutesDecimal);
                const seconds = ((minutesDecimal - minutes) * 60).toFixed(2);

                const direction = isLat
                    ? (decimal >= 0 ? 'N' : 'S')
                    : (decimal >= 0 ? 'E' : 'W');

                return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
            }

            // Helper function to format coordinates
            function formatCoord(lat: number, lon: number, format: "degrees" | "dms"): { lat: string; lon: string } {
                if (format === "dms") {
                    return {
                        lat: toDMS(lat, true),
                        lon: toDMS(lon, false)
                    };
                } else {
                    return {
                        lat: lat.toFixed(6),
                        lon: lon.toFixed(6)
                    };
                }
            }

            function clearCoordMode() {
                coordMode = false;
                measurementDiv.style.display = "";
                coordDiv.style.display = "none";

                // Reset pin coordinates to ---
                const pinLat = coordDiv.querySelector(".coord-pin-lat");
                const pinLon = coordDiv.querySelector(".coord-pin-lon");
                if (pinLat) pinLat.textContent = "---";
                if (pinLon) pinLon.textContent = "---";
                lastPinLat = 0;
                lastPinLon = 0;

                // Remove pin graphic from map
                if (coordPinGraphic && highlightLayerRef.current) {
                    highlightLayerRef.current.remove(coordPinGraphic);
                    coordPinGraphic = null;
                }

                // Remove handlers
                if (pointerMoveHandler) {
                    pointerMoveHandler.remove();
                    pointerMoveHandler = null;
                }
                if (clickHandler) {
                    clickHandler.remove();
                    clickHandler = null;
                }
            }

            function startOrToggle(tool: "distance" | "area") {
                clearCoordMode();
                if (m.activeTool === tool) {
                    m.clear();
                    m.activeTool = null;
                    return;
                }

                m.clear();
                m.activeTool = null;
                requestAnimationFrame(() => {
                    m.activeTool = tool;
                });
            }

            function toggleCoordMode() {
                coordMode = !coordMode;

                if (coordMode) {
                    m.clear();
                    m.activeTool = null;
                    measurementDiv.style.display = "none";
                    coordDiv.style.display = "block";

                    // Reset pin coordinates to ---
                    const pinLat = coordDiv.querySelector(".coord-pin-lat");
                    const pinLon = coordDiv.querySelector(".coord-pin-lon");
                    if (pinLat) pinLat.textContent = "---";
                    if (pinLon) pinLon.textContent = "---";

                    // Add format selector handler
                    const formatSelect = coordDiv.querySelector(".coord-format-select") as HTMLSelectElement;
                    if (formatSelect) {
                        formatSelect.addEventListener("change", () => {
                            coordFormat = formatSelect.value as "degrees" | "dms";

                            // Update pin coordinates if they exist
                            if (lastPinLat !== 0 || lastPinLon !== 0) {
                                const formatted = formatCoord(lastPinLat, lastPinLon, coordFormat);
                                const pinLat = coordDiv.querySelector(".coord-pin-lat");
                                const pinLon = coordDiv.querySelector(".coord-pin-lon");
                                if (pinLat) pinLat.textContent = formatted.lat;
                                if (pinLon) pinLon.textContent = formatted.lon;
                            }
                        });
                    }

                    // Add pointer-move handler
                    pointerMoveHandler = view.on("pointer-move", (event: any) => {
                        const point = view.toMap({ x: event.x, y: event.y });
                        if (point && point.latitude != null && point.longitude != null) {
                            const formatted = formatCoord(point.latitude, point.longitude, coordFormat);
                            const mouseLat = coordDiv.querySelector(".coord-mouse-lat");
                            const mouseLon = coordDiv.querySelector(".coord-mouse-lon");
                            if (mouseLat) mouseLat.textContent = formatted.lat;
                            if (mouseLon) mouseLon.textContent = formatted.lon;
                        }
                    });

                    // Add click handler to pin location
                    clickHandler = view.on("click", (event: any) => {
                        const point = view.toMap({ x: event.x, y: event.y });
                        if (point && point.latitude != null && point.longitude != null) {
                            lastPinLat = point.latitude;
                            lastPinLon = point.longitude;

                            const formatted = formatCoord(point.latitude, point.longitude, coordFormat);
                            const pinLat = coordDiv.querySelector(".coord-pin-lat");
                            const pinLon = coordDiv.querySelector(".coord-pin-lon");
                            if (pinLat) pinLat.textContent = formatted.lat;
                            if (pinLon) pinLon.textContent = formatted.lon;

                            // Remove previous pin graphic if it exists
                            if (coordPinGraphic && highlightLayerRef.current) {
                                highlightLayerRef.current.remove(coordPinGraphic);
                            }

                            // Create and add new pin graphic to the map
                            if (highlightLayerRef.current) {
                                const pinSymbol = new PictureMarkerSymbol({
                                    url: esriGreenPin,
                                    width: 16,
                                    height: 26,
                                    yoffset: 13
                                });

                                coordPinGraphic = new Graphic({
                                    geometry: point,
                                    symbol: pinSymbol
                                });

                                highlightLayerRef.current.add(coordPinGraphic);
                            }
                        }
                    });
                } else {
                    measurementDiv.style.display = "";
                    coordDiv.style.display = "none";

                    // Remove pin graphic from map
                    if (coordPinGraphic && highlightLayerRef.current) {
                        highlightLayerRef.current.remove(coordPinGraphic);
                        coordPinGraphic = null;
                    }

                    // Remove handlers
                    if (pointerMoveHandler) {
                        pointerMoveHandler.remove();
                        pointerMoveHandler = null;
                    }
                    if (clickHandler) {
                        clickHandler.remove();
                        clickHandler = null;
                    }
                }

                // Update button state
                btnCoord.setAttribute("aria-pressed", String(coordMode));
                if (coordMode) {
                    btnDist.setAttribute("aria-pressed", "false");
                    btnArea.setAttribute("aria-pressed", "false");
                    view.popupEnabled = false;
                    (view as any).__measuring = true;
                }
            }

            btnDist.onclick = () => startOrToggle("distance");
            btnArea.onclick = () => startOrToggle("area");
            btnCoord.onclick = () => toggleCoordMode();
            btnClear.onclick = () => {
                m.clear();
                m.activeTool = null;
                coordMode = false;
                measurementDiv.style.display = "";
                coordDiv.style.display = "none";

                // Reset pin coordinates to ---
                const pinLat = coordDiv.querySelector(".coord-pin-lat");
                const pinLon = coordDiv.querySelector(".coord-pin-lon");
                if (pinLat) pinLat.textContent = "---";
                if (pinLon) pinLon.textContent = "---";
                lastPinLat = 0;
                lastPinLon = 0;

                // Remove pin graphic from map
                if (coordPinGraphic && highlightLayerRef.current) {
                    highlightLayerRef.current.remove(coordPinGraphic);
                    coordPinGraphic = null;
                }

                // Remove handlers
                if (pointerMoveHandler) {
                    pointerMoveHandler.remove();
                    pointerMoveHandler = null;
                }
                if (clickHandler) {
                    clickHandler.remove();
                    clickHandler = null;
                }
            };

            m.watch("activeTool", (tool: "distance" | "area" | null) => {
                btnDist.setAttribute("aria-pressed", String(tool === "distance"));
                btnArea.setAttribute("aria-pressed", String(tool === "area"));
                btnCoord.setAttribute("aria-pressed", "false");
                if (!coordMode) {
                    view.popupEnabled = !tool;
                    (view as any).__measuring = !!tool;
                }
            });

            (measurement as any).watch("activeTool", (tool: "distance" | "area" | null) => {
                if (!coordMode) {
                    view.popupEnabled = !tool;
                    (view as any).__measuring = !!tool;
                }
            });

            measureExpand.watch("expanded", (open: boolean) => {
                if (open) {
                    requestAnimationFrame(() => m.renderNow?.());
                } else {
                    m.clear();
                    m.activeTool = null;
                    coordMode = false;
                    measurementDiv.style.display = "";
                    coordDiv.style.display = "none";
                    view.popupEnabled = true;
                    btnDist.setAttribute("aria-pressed", "false");
                    btnArea.setAttribute("aria-pressed", "false");
                    btnCoord.setAttribute("aria-pressed", "false");

                    // Reset pin coordinates to ---
                    const pinLat = coordDiv.querySelector(".coord-pin-lat");
                    const pinLon = coordDiv.querySelector(".coord-pin-lon");
                    if (pinLat) pinLat.textContent = "---";
                    if (pinLon) pinLon.textContent = "---";
                    lastPinLat = 0;
                    lastPinLon = 0;

                    // Remove pin graphic from map
                    if (coordPinGraphic && highlightLayerRef.current) {
                        highlightLayerRef.current.remove(coordPinGraphic);
                        coordPinGraphic = null;
                    }

                    // Remove handlers
                    if (pointerMoveHandler) {
                        pointerMoveHandler.remove();
                        pointerMoveHandler = null;
                    }
                    if (clickHandler) {
                        clickHandler.remove();
                        clickHandler = null;
                    }
                }
            });

            const printExpand = new Expand({ view, content: printWidgetContainer, group: "top-right-tools", expandTooltip: "Print", expandIcon: "print", collapseIcon: "print", placement: widgetPlacement, container: wrap(), mode: "floating" });

            // Rename "Export"/"Exports" labels in the Print widget to "Print"/"Prints"
            function fixPrintWidgetLabels() {
                const printContainer = printExpand.container as HTMLElement | null;
                if (!printContainer) return;
                const walker = document.createTreeWalker(printContainer, NodeFilter.SHOW_TEXT);
                let node: Text | null;
                while ((node = walker.nextNode() as Text | null)) {
                    if (node.nodeValue === 'Export') node.nodeValue = 'Print';
                    else if (node.nodeValue === 'Exports') node.nodeValue = 'Prints';
                }
            }

            // Fix calcite-action button flex property to center icons
            function fixCalciteActionButtons() {
                const calciteActions = document.querySelectorAll('calcite-action');
                calciteActions.forEach((action) => {
                    const shadowRoot = (action as any).shadowRoot;
                    if (shadowRoot) {
                        const button = shadowRoot.querySelector('.button');
                        if (button && !(button as any).__flexFixed) {
                            (button as HTMLElement).style.flex = 'none';
                            (button as HTMLElement).style.justifyContent = 'center';
                            (button as any).__flexFixed = true;
                        }
                    }
                });
            }

            // Run immediately and after delays to catch dynamically added elements
            setTimeout(fixCalciteActionButtons, 100);
            setTimeout(fixCalciteActionButtons, 500);
            setTimeout(fixCalciteActionButtons, 1000);
            setTimeout(fixPrintWidgetLabels, 100);
            setTimeout(fixPrintWidgetLabels, 500);
            setTimeout(fixPrintWidgetLabels, 1000);

            // Watch for new calcite-action elements being added (throttled to avoid firing on every map DOM update)
            let mutationTimer: ReturnType<typeof setTimeout> | null = null;
            const observer = new MutationObserver(() => {
                if (mutationTimer) return;
                mutationTimer = setTimeout(() => {
                    mutationTimer = null;
                    fixCalciteActionButtons();
                    fixPrintWidgetLabels();
                }, 200);
            });
            observer.observe(document.body, { childList: true, subtree: true });

            const coord = installCompactCoordinate(view, {
                decimals: 6,
                pinImageUrl: require("./custom-widgets/images/esriGreenPin16x26.png") as string,
                attachToUi: false
            });

            view.ui.add(coord.element as any, "manual");

            const pillEl = coord.element as HTMLElement;
            Object.assign(pillEl.style, { position: "absolute", left: "8px", bottom: "70px", zIndex: "5" });

            const vc = view.container as HTMLElement | null;
            const sbEl = vc ? (vc.querySelector(".esri-scale-bar") as HTMLElement | null) : null;

            function placeAboveScale() {
                if (!vc || !sbEl) return;
                const cr = vc.getBoundingClientRect();
                const sr = sbEl.getBoundingClientRect();
                const bottomDistance = Math.max(0, cr.bottom - sr.top);
                const gap = 12;
                pillEl.style.bottom = `${bottomDistance + gap}px`;
            }

            placeAboveScale();
            const ro1 = new ResizeObserver(placeAboveScale);
            const ro2 = new ResizeObserver(placeAboveScale);
            if (sbEl) ro1.observe(sbEl);
            if (vc) ro2.observe(vc);
            window.addEventListener("resize", placeAboveScale);
            const uiHandle = view.watch("size", placeAboveScale);

            (view as any).__widgetCleanup = () => {
                observer.disconnect();
                if (mutationTimer) clearTimeout(mutationTimer);

                try { view.ui.remove(gsvExpand); } catch { }
                gsvExpand.destroy();
                gsv.destroy();

                printExpand.destroy(); legendExpand.destroy(); llExpand.destroy(); bgExpand.destroy();
                measureExpand.destroy(); measurement.destroy();
                printEl.destroy(); legend.destroy(); layerList.destroy(); basemapGallery.destroy();

                try { ro1.disconnect(); ro2.disconnect(); } catch { }
                uiHandle.remove();
                coord.destroy();

                scaleBar.destroy();
                styleEl.remove();
            };
        }, (err: any) => {
            if (err?.name === "AbortError" || err?.message === "Aborted") return;
            console.error("[ArcGISMap] view.when error", err);
        });

        return () => {
            const v = viewRef.current as any;
            if (v && typeof v.__widgetCleanup === "function") v.__widgetCleanup();
            view.destroy();
        };
    }, [itemId]);

    useImperativeHandle(ref, () => ({
        zoomTo({ latitude, longitude }) {
            viewRef.current?.goTo({ center: [longitude, latitude], zoom: 18 }).catch(() => { });
        },
        async highlightAndZoom(geomJson?: any, center?: { latitude: number; longitude: number }) {
            const view = viewRef.current;
            const hl = highlightLayerRef.current;
            if (!view || !hl) return;
            hl.removeAll();
            if (geomJson) {
                const geometry = geometryJsonUtils.fromJSON(geomJson);

                // If it's a point, query for the parcel at that location
                if (geometry?.type === "point") {
                    try {
                        const webmap = view.map as WebMap;
                        await webmap.loadAll();

                        const parcelLayer = webmap.allLayers.find((layer: any) => {
                            const url = layer.url || "";
                            return layer.title?.toLowerCase().includes("parcel");
                        }) as FeatureLayer;

                        if (parcelLayer) {
                            const query = parcelLayer.createQuery();
                            query.geometry = geometry;
                            query.spatialRelationship = "intersects";
                            query.returnGeometry = true;
                            query.outFields = ["*"];

                            const results = await parcelLayer.queryFeatures(query);

                            if (results.features.length > 0) {
                                const parcelGeometry = results.features[0].geometry;
                                const symbol = new SimpleFillSymbol({
                                    color: new Color([0, 121, 193, 0.2]),
                                    outline: new SimpleLineSymbol({
                                        color: new Color([0, 121, 193, 1]),
                                        width: 2
                                    }),
                                });
                                const g = new Graphic({ geometry: parcelGeometry, symbol });
                                hl.add(g);
                                // Zoom to fit the geometry extent instead of fixed zoom level
                                if (parcelGeometry) view.goTo({ target: parcelGeometry }).catch(() => { });
                                return;
                            }
                        }
                    } catch (error) {
                        console.error("Error querying parcel:", error);
                    }

                    // Fallback: just zoom to the point with a reasonable zoom level
                    view.goTo({ target: geometry, zoom: 16 }).catch(() => { });
                    return;
                }

                const symbol = new SimpleFillSymbol({
                    color: new Color([0, 121, 193, 0.2]),
                    outline: new SimpleLineSymbol({
                        color: new Color([0, 121, 193, 1]),
                        width: 2
                    }),
                });
                const g = new Graphic({ geometry, symbol });
                hl.add(g);
                if (geometry) view.goTo({ target: geometry }).catch(() => { });
                return;
            }
            if (center) {
                view.goTo({ center: [center.longitude, center.latitude], zoom: 16 }).catch(() => { });
            }
        },
        async queryParcelAtPoint(point: Point, view: MapView, hl: GraphicsLayer) {
            try {
                const webmap = view.map as WebMap;
                await webmap.loadAll();

                const parcelLayer = webmap.allLayers.find((layer: any) => {
                    const url = layer.url || "";
                    return layer.title?.toLowerCase().includes("parcel");
                }) as FeatureLayer;

                if (!parcelLayer) {
                    console.warn("Parcel layer not found");
                    view.goTo({ target: point, zoom: 18 }).catch(() => { });
                    return;
                }

                const query = parcelLayer.createQuery();
                query.geometry = point;
                query.spatialRelationship = "intersects";
                query.returnGeometry = true;
                query.outFields = ["*"];

                const results = await parcelLayer.queryFeatures(query);

                if (results.features.length > 0) {
                    const parcelGeometry = results.features[0].geometry;
                    const symbol = new SimpleFillSymbol({
                        color: new Color([0, 121, 193, 0.2]),
                        outline: new SimpleLineSymbol({
                            color: new Color([0, 121, 193, 1]),
                            width: 2
                        }),
                    });
                    const g = new Graphic({ geometry: parcelGeometry, symbol });
                    hl.add(g);
                    if (parcelGeometry) view.goTo({ target: parcelGeometry, zoom: 18 }).catch(() => { });
                } else {
                    view.goTo({ target: point, zoom: 18 }).catch(() => { });
                }
            } catch (error) {
                console.error("Error querying parcel:", error);
                view.goTo({ target: point, zoom: 18 }).catch(() => { });
            }
        },
        async getParcelLayerUrl(): Promise<string | null> {
            const view = viewRef.current;
            if (!view) return null;

            try {
                const webmap = view.map as WebMap;
                await webmap.loadAll();

                const parcelLayer = webmap.allLayers.find((layer: any) => {
                    return layer.title?.toLowerCase().includes("parcel");
                }) as FeatureLayer;

                if (!parcelLayer) return null;

                // Get the layer URL and layer ID
                const baseUrl = parcelLayer.url;
                const layerId = (parcelLayer as any).layerId;

                // If layerId is available and URL doesn't already include it, append it
                if (layerId !== undefined && baseUrl && !baseUrl.match(/\/\d+$/)) {
                    return `${baseUrl}/${layerId}`;
                }

                return baseUrl || null;
            } catch (error) {
                console.error("Error getting parcel layer URL:", error);
                return null;
            }
        }
    }));

    useEffect(() => {
        if (!viewRef.current) return;
        const view = viewRef.current;

        const isParcelOrBuilding = (layer: any): { ok: boolean; id?: number; title?: string } => {
            try {
                const title = (layer?.title ?? "").toString().toLowerCase();
                const url: string = String(layer?.url ?? "");
                const idPart = url.split("/").filter(Boolean).pop();
                const parsedId = Number(idPart);
                const isParcel = parsedId === 41 || title.includes("parcel");
                const isBuilding = title.includes("building");
                return { ok: isParcel || isBuilding, id: Number.isFinite(parsedId) ? parsedId : undefined, title: layer?.title };
            } catch {
                return { ok: false };
            }
        };

        // Some counties (e.g. Hattiesburg) render their parcel layer as tiles, which
        // view.hitTest() cannot pick up graphics from. When hitTest finds nothing usable,
        // fall back to querying the configured parcel layer directly by the clicked geometry.
        const queryParcelFallback = async (mp: __esri.Point, center: { latitude: number; longitude: number }) => {
            if (!config.parcelLayer) return false;
            try {
                const queryObj = new Query();
                queryObj.geometry = mp;
                queryObj.spatialRelationship = "intersects";
                queryObj.returnGeometry = true;
                queryObj.outFields = ["*"];
                queryObj.num = 1;

                const response = await queryModule.executeQueryJSON(config.parcelLayer, queryObj);
                const feature = response.features?.[0];
                if (!feature) return false;

                const geomJson = feature.geometry?.toJSON?.();
                const attrs = feature.attributes ?? {};
                const idPart = config.parcelLayer.split("/").filter(Boolean).pop();
                const parsedId = Number(idPart);

                onMapIdentifyRef.current?.({
                    center,
                    geometryJson: geomJson,
                    attributes: attrs,
                    layerId: Number.isFinite(parsedId) ? parsedId : undefined,
                    layerTitle: "Parcels",
                    layerUrl: config.parcelLayer,
                });

                try { (ref as any)?.current?.highlightAndZoom?.(geomJson, center); } catch { }
                return true;
            } catch (err) {
                console.error("Parcel fallback query error:", err);
                return false;
            }
        };

        const onClick = async (evt: ViewClickEvent) => {
            const v: any = view;
            if (!view || v.__measuring || v.__drawing) return;

            const mp = evt.mapPoint;
            if (!mp) return;

            const center = toLatLong(mp);

            let ht;
            try {
                ht = await view.hitTest(evt);
            } catch {
                ht = null;
            }
            if (!ht || !ht.results?.length) {
                await queryParcelFallback(mp, center);
                return;
            }

            const parcelOrBuildingResult = ht.results.find(r => {
                const layer = (r as any)?.graphic?.layer;
                return layer && isParcelOrBuilding(layer).ok;
            });

            // If no parcel/building layer was hit, try the direct parcel query before giving up
            if (!parcelOrBuildingResult) {
                await queryParcelFallback(mp, center);
                return;
            }

            const g: Graphic | undefined = (parcelOrBuildingResult as any)?.graphic;
            if (!g) return;

            // Suppress the native popup for parcel/building clicks since we handle those in the sidebar
            (view as any).closePopup?.();

            let geomJson = g.geometry?.toJSON?.();
            const layerInfo = isParcelOrBuilding((g as any).layer);
            const layerUrl = ((g as any).layer as FeatureLayer)?.url;

            let attrs = g.attributes ?? {};

            try {
                const layer = (g as any).layer as FeatureLayer;
                if (layer && layer.queryFeatures) {
                    const query = layer.createQuery();

                    // Query by OBJECTID to get the exact feature, not by geometry which can match multiple parcels
                    const objectId = attrs.OBJECTID || attrs.ObjectId || attrs.objectid || attrs.FID;

                    if (objectId !== undefined) {
                        query.objectIds = [objectId];
                        query.returnGeometry = true;
                        query.outFields = ["*"];

                        const results = await layer.queryFeatures(query);
                        if (results.features.length > 0) {
                            attrs = results.features[0].attributes ?? {};
                            // Also update the geometry to ensure we have the full polygon
                            if (results.features[0].geometry) {
                                const newGeomJson = results.features[0].geometry.toJSON?.();
                                geomJson = newGeomJson || geomJson;
                            }
                        } else {
                            console.warn('Query returned no features for OBJECTID:', objectId);
                        }
                    } else {
                        console.warn('No OBJECTID found in graphic attributes, using graphic attributes as-is');
                    }
                } else {
                    console.warn('Cannot query layer:', {
                        hasLayer: !!layer,
                        hasQueryFeatures: !!(layer && layer.queryFeatures)
                    });
                }
            } catch (error) {
                console.error('Failed to query layer for attributes:', error);
            }

            onMapIdentifyRef.current?.({
                center,
                geometryJson: geomJson,
                attributes: attrs,
                layerId: layerInfo.id,
                layerTitle: layerInfo.title,
                layerUrl: layerUrl || undefined,
            });

            try {
                (ref as any)?.current?.highlightAndZoom?.(geomJson, center);
            } catch { }
        };

        const handle = view.on("click", onClick);
        return () => handle.remove();
    }, [itemId]);

    return <div style={MapDivStyle} ref={mapDiv} />;
});

export default ArcGISMap;

function toLatLong(p: Point): { latitude: number; longitude: number } {
    const g = (p.spatialReference?.isWGS84 || p.spatialReference?.isGeographic)
        ? p
        : (webMercatorUtils.webMercatorToGeographic(p) as Point);

    return { latitude: g.y as number, longitude: g.x as number };
}
